import type { SourceSystem } from '@atlas/shared';
import { SOURCE_SYSTEM_LABELS } from '@atlas/shared';

type BadgeColor = { text: string; bg: string };

/**
 * One fixed colour per source system, so a system always renders the same
 * capsule everywhere in the app. Each pair is dark text on a soft background,
 * Notion-style, with hues spread so neighbouring systems stay distinct. The
 * full class strings are written out literally so Tailwind's scanner keeps
 * them in the build (do not build these class names dynamically).
 */
const SOURCE_SYSTEM_COLORS: Record<SourceSystem, BadgeColor> = {
  adapt_rec: { text: 'text-[#2f8a3b]', bg: 'bg-[#e6f4e7]' }, // green
  bullhorn: { text: 'text-[#2563eb]', bg: 'bg-[#e7effd]' }, // blue
  ezekia: { text: 'text-[#4f46e5]', bg: 'bg-[#eae9fc]' }, // indigo
  filefinder: { text: 'text-[#0369a1]', bg: 'bg-[#e3f0f9]' }, // sky
  firefish: { text: 'text-[#c0392b]', bg: 'bg-[#fbe9e7]' }, // red
  futurelink: { text: 'text-[#1e3a8a]', bg: 'bg-[#e4e8f4]' }, // navy
  gem: { text: 'text-[#a21caf]', bg: 'bg-[#fae6fb]' }, // fuchsia
  invenias: { text: 'text-[#4d7c0f]', bg: 'bg-[#ecf5db]' }, // lime
  itris: { text: 'text-[#92603b]', bg: 'bg-[#f3ece4]' }, // brown
  jobadder: { text: 'text-[#b45309]', bg: 'bg-[#fbf0dc]' }, // orange
  job_science: { text: 'text-[#9f1239]', bg: 'bg-[#fbe4ea]' }, // wine
  loxo: { text: 'text-[#be185d]', bg: 'bg-[#fce6ef]' }, // magenta
  manatal: { text: 'text-[#0891b2]', bg: 'bg-[#e0f2f7]' }, // cyan
  microdec: { text: 'text-[#115e59]', bg: 'bg-[#dbeeeb]' }, // pine
  pc_recruiter: { text: 'text-[#a16207]', bg: 'bg-[#f8f1d9]' }, // amber
  recruit_crm: { text: 'text-[#6d28d9]', bg: 'bg-[#efe9fd]' }, // violet
  recruitee_crm: { text: 'text-[#6b21a8]', bg: 'bg-[#f0e6fb]' }, // grape
  recruiterflow: { text: 'text-[#be123c]', bg: 'bg-[#fce7eb]' }, // pink-red
  salesforce: { text: 'text-[#0284c7]', bg: 'bg-[#e0f0fa]' }, // cerulean
  seven20: { text: 'text-[#6f7212]', bg: 'bg-[#f3f4d6]' }, // yellow
  shadow_one: { text: 'text-[#475569]', bg: 'bg-[#eef1f5]' }, // slate
  targetrecruit: { text: 'text-[#c2410c]', bg: 'bg-[#fdebdd]' }, // orange-red
  thrive: { text: 'text-[#9333ea]', bg: 'bg-[#f4e9fd]' }, // purple
  top_echelon: { text: 'text-[#166534]', bg: 'bg-[#e2f0e6]' }, // forest
  tracker: { text: 'text-[#334155]', bg: 'bg-[#eaedf1]' }, // charcoal
  vincere: { text: 'text-[#0f766e]', bg: 'bg-[#def2ef]' }, // emerald
  voyager_infinity: { text: 'text-[#0e7490]', bg: 'bg-[#def0f4]' }, // teal
};

/** Fallback for a slug not in the list, so unknown sources stay readable. */
const FALLBACK: BadgeColor = { text: 'text-[#73726e]', bg: 'bg-[#f1f1ef]' };

/** The capsule colours for a source system (for dots, borders, etc.). */
export function sourceBadgeColor(system: string | undefined | null): BadgeColor {
  if (!system) return FALLBACK;
  return SOURCE_SYSTEM_COLORS[system as SourceSystem] ?? FALLBACK;
}

export function SourceBadge({ system }: { system: string | undefined | null }) {
  if (!system) return <span className="text-xs text-[#aeaaa4]">—</span>;
  const label = SOURCE_SYSTEM_LABELS[system as SourceSystem] ?? system;
  const cfg = sourceBadgeColor(system);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cfg.bg} ${cfg.text}`}
    >
      {label}
    </span>
  );
}
